import type { AppliedFilterInterface } from '@shopify/polaris';

import { FILTER_TAB_TEMPLATES, TAB_TEMPLATES } from '~/constants/template';
import { useCallback, useMemo, useState } from 'react';
import { TemplateType } from '~/types/template';
import { EMetricKey } from '~/types/metric';

export const PAGE_ANALYTIC_TABS = [{ key: TemplateType.ALL, name: 'All' }, ...TAB_TEMPLATES];

export const useInitializePageAnalyticFilter = (defaultSortValue?: string) => {
  const [queryValue, setQueryValue] = useState<string | undefined>(undefined);
  const [tabSelected, setTabSelected] = useState<number>(0);
  const [pageTypes, setPageTypes] = useState<TemplateType[] | undefined>(undefined);
  const [sortSelected, setSortSelected] = useState<string[]>([
    `${defaultSortValue || EMetricKey.SESSION} asc`,
  ]);

  const handlePageTypeChange = useCallback((value: TemplateType[]) => setPageTypes(value), []);
  const handleFiltersQueryChange = useCallback((value: string) => setQueryValue(value), []);
  const handleSortChange = useCallback((value: string[]) => setSortSelected(value), []);
  const handleTabChange = useCallback((value: number) => setTabSelected(value), []);
  const handlePageTypeRemove = useCallback(() => setPageTypes(undefined), []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(undefined), []);
  const handleFiltersClearAll = useCallback(() => {
    handlePageTypeRemove();
    handleQueryValueRemove();
  }, [handlePageTypeRemove, handleQueryValueRemove]);

  const filterPageTypes = useMemo<TemplateType[]>(() => {
    // Only for the View Your Insights modal
    if (pageTypes?.length) return pageTypes;

    // Only for the page analytics listing
    const pageType = PAGE_ANALYTIC_TABS[tabSelected]?.key;
    if (!pageType || pageType === TemplateType.ALL) return [];
    return [pageType];
  }, [tabSelected, pageTypes]);

  const appliedFilters: AppliedFilterInterface[] = useMemo(() => {
    if (!pageTypes?.length) return [];

    return [
      {
        key: 'pageType',
        label: disambiguateLabel(pageTypes),
        onRemove: handlePageTypeRemove,
      },
    ];
  }, [handlePageTypeRemove, pageTypes]);

  return {
    queryValue,
    pageTypes,
    tabSelected,
    filterPageTypes,
    appliedFilters,
    sortSelected,
    handleTabChange,
    handleFiltersClearAll,
    handleFiltersQueryChange,
    handlePageTypeChange,
    handleQueryValueRemove,
    handleSortChange,
    handlePageTypeRemove,
  };

  function disambiguateLabel(pageTypes: TemplateType[] | undefined) {
    return (FILTER_TAB_TEMPLATES.filter((item) => pageTypes?.includes(item.value as any))
      .map((item) => item.label)
      .join(', ') || 'Page type') as string;
  }
};

export type IInitializePageAnalyticFilter = ReturnType<typeof useInitializePageAnalyticFilter>;
